import { Button, TextField } from '@mui/material';
import { createUserWithEmailAndPassword, getAuth, sendEmailVerification } from 'firebase/auth';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { handleError } from '../../../common/firebase/auth-error.service';
import { emailRegex, passwordRegex } from '../../../common/firebase/regex';
import { severity } from '../../../common/models/severity.enum';
import { ServiceManager } from '../../../services/service-manager';
import styles from './SignUp.module.css';

interface SignUpProps {
    privacyStatementLink: string;
    termsAndConditionsLink: string;
}

const SignUp: FC<SignUpProps> = ({privacyStatementLink, termsAndConditionsLink}) => {

    const { register, handleSubmit, formState: { errors }, setError } = useForm<{ email: string, password: string, repeatPassword: string }>();
    const [verificationSent, setVerificationSent] = useState<boolean>(false)
    const [userEmail, setUserEmail] = useState<string>('')

    const navigate = useNavigate();

    const onSubmit = (data) => {
        const auth = getAuth();
        if (data.password === data.repeatPassword && data.password.length >= 6) {
            createUserWithEmailAndPassword(auth, data.email, data.password)
                .then(async () => {
                    ServiceManager.snackbarService.openSnackbar('Uw account is aangemaakt.', severity.success, 3000);
                    sendEmailVerification(auth.currentUser).then(() => {
                        setVerificationSent(true);
                        setUserEmail(data.email);
                    }).catch(error => {
                        handleError(error);
                    });
                })
                .catch((error) => {
                    handleError(error);
                });
        } else {
            setError('repeatPassword', { type: 'password-mismatch', message: 'De wachtwoorden komen niet overeen. Controleer ze en probeer het opnieuw.' });
        }
    }

    const send = () => {
        const auth = getAuth();
        sendEmailVerification(auth.currentUser).then(() => {
            ServiceManager.snackbarService.openSnackbar(`Uw verificatie e-mail is verstuurd naar ${auth.currentUser.email}.`, severity.success);
        }).catch(error => {
            handleError(error);
        })
    }

    return (
        <div className={styles.SignUp} data-testid="SignUp">
            {!verificationSent ?
                <>
                    <form data-testid="signup-form" onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <TextField id="signup-email"
                                fullWidth
                                inputProps={{
                                    "data-testid": "signup-email",
                                }}
                                className="form-input"
                                label="E-mailadres"
                                {...register('email', {
                                    required: { value: true, message: 'U moet uw e-mailadres invullen.' }, pattern: {
                                        value: emailRegex,
                                        message: 'Het e-mailadres voldoet niet aan de regels.'
                                    }
                                })}
                                variant="standard"
                            />
                            {errors.email && <p className="input-error">{errors.email.message}</p>}
                        </div>
                        <div>
                            <TextField id="signup-password"
                                inputProps={{
                                    "data-testid": "signup-password",
                                }}
                                fullWidth
                                className="form-input"
                                label="Wachtwoord"
                                {...register('password', {
                                    required: { value: true, message: 'U moet uw wachtwoord invullen.' },
                                    pattern: {
                                        value: passwordRegex,
                                        message: 'Het wachtwoord moet minimaal 8 karakters, 2 hoofdletters, 1 speciaal teken, 2 cijfers en 3 kleine letters bevatten.'
                                    }
                                })}
                                variant="standard"
                                type="password"
                            />
                            {errors.password && <p className="input-error">{errors.password.message}</p>}
                        </div>
                        <div>
                            <TextField id="signup-repeat-password"
                                inputProps={{
                                    "data-testid": "signup-repeat-password",
                                }}
                                fullWidth
                                className="form-input"
                                label="Wachtwoord Herhalen"
                                {...register('repeatPassword', {
                                    required: { value: true, message: 'U moet uw wachtwoord herhalen.' },
                                    pattern: {
                                        value: passwordRegex,
                                        message: 'Het wachtwoord moet minimaal 8 karakters, 2 hoofdletters, 1 speciaal teken, 2 cijfers en 3 kleine letters bevatten.'
                                    }
                                })}
                                variant="standard"
                                type="password"
                            />
                            {errors.repeatPassword && <p className="input-error">{errors.repeatPassword.message}</p>}
                        </div>
                        <div className={styles.actions}>
                            <Button color="primary" data-testid="signup-submit" variant="contained" type="submit">Account Aanmaken</Button>
                            <Button color="secondary" data-testid="login-submit" variant="outlined" onClick={() => navigate('/auth/login')}>Login</Button>
                        </div>
                    </form>
                    <p>
                        <em>
                            Door een account aan te maken gaat u akkoord met onze <a target="_blank" rel="noreferrer" href={termsAndConditionsLink}>algemene gebruiksvoorwaarden</a> en <a target="_blank" rel="noreferrer" href={privacyStatementLink}>privacy statement</a>
                        </em>
                    </p>
                </> :
                <div className={styles.message}>
                    <p>
                        Uw account is aangemaakt, we hebben een <b>verificatie e-mail</b> verstuurd naar <b>{userEmail}</b>. Als u binnen een paar minuten de e-mail niet heeft ontvangen controleer dan uw spambox of probeer het nog een keer via onderstaande knop.
                    </p>
                    <div className={styles.actions}>
                        <Button data-testid="verify-button" color="secondary" variant="outlined" onClick={send}>Opnieuw verzenden</Button>
                    </div>
                </div>
            }
        </div>
    )
};

export default SignUp;